/**
 * Store de session — Phase 4, incrément 4.3.
 *
 * Source de vérité pour « suis-je connecté ? ». L'AppShell s'y
 * abonne pour redessiner quand le statut change (badge utilisateur,
 * bouton login → logout, etc.).
 *
 * Cycle de vie :
 *
 *   1. Au boot, `initSession()` appelle `GET /auth/me` :
 *      - 200 → `status: 'authenticated'`, user fetché.
 *      - 401 → `status: 'anonymous'`.
 *      - Erreur réseau (API down, offline) → `status: 'anonymous'`,
 *        avec un flag `offline: true` qu'on pourra exploiter
 *        (futur Phase 4.4+ : afficher « connexion manuelle »).
 *
 *   2. `login()` et `register()` mettent le store à
 *      `authenticated` après succès. `logout()` le repasse à
 *      `anonymous`. Échec → state inchangé, l'appelant gère
 *      l'erreur (toast / inline form error).
 *
 * **Mode local-only préservé** (CLAUDE.md §2.4) — l'app fonctionne
 * complètement sans backend. Le store reste à `anonymous` et
 * l'utilisateur peut continuer en mode purement local.
 */
import { createStore } from '../lib/store.js';
import * as authApi from '../api/auth.js';
import { ApiError } from '../api/client.js';
import type { AuthUser, LoginInput, RegisterInput } from '../api/auth.js';

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

export interface SessionState {
  status: SessionStatus;
  user: AuthUser | null;
  /**
   * `true` quand la dernière tentative d'init a échoué côté réseau
   * (vs 401 explicite). Permet à l'UI de différencier « pas de
   * compte » vs « API joignable ».
   */
  offline: boolean;
}

const initialState: SessionState = {
  status: 'loading',
  user: null,
  offline: false,
};

const store = createStore<SessionState>(initialState);

export function getSession(): SessionState {
  return store.getState();
}

export function subscribeSession(listener: (s: SessionState) => void): () => void {
  return store.subscribe(listener);
}

/**
 * Tente de récupérer la session courante via `/auth/me`. Appelée
 * une seule fois au boot par `main.ts`. Si l'utilisateur se connecte
 * ensuite via `loginAndUpdateSession`, le store sera mis à jour
 * directement — pas besoin de re-call `initSession`.
 *
 * Idempotent : appel multiple = no-op après le premier (le state
 * reste à `loading` jusqu'à la première résolution).
 */
let initStarted = false;

export async function initSession(): Promise<void> {
  if (initStarted) return;
  initStarted = true;
  try {
    const user = await authApi.me();
    store.setState({ status: 'authenticated', user, offline: false });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      store.setState({ status: 'anonymous', user: null, offline: false });
    } else {
      // Erreur réseau / 503 / autre — on retombe à anonymous mais
      // on signale `offline: true` pour l'UI.
      store.setState({ status: 'anonymous', user: null, offline: true });
    }
  }
}

/**
 * Login + mise à jour du store. Throw `ApiError` sur 401, le caller
 * gère l'affichage de l'erreur dans le form.
 */
export async function loginAndUpdateSession(input: LoginInput): Promise<AuthUser> {
  const user = await authApi.login(input);
  store.setState({ status: 'authenticated', user, offline: false });
  return user;
}

/**
 * Register + mise à jour du store. Throw `ApiError` sur 409
 * `email_taken` ou 400 validation.
 */
export async function registerAndUpdateSession(input: RegisterInput): Promise<AuthUser> {
  const user = await authApi.register(input);
  store.setState({ status: 'authenticated', user, offline: false });
  return user;
}

/**
 * Logout côté serveur (clear cookie) + reset store local. Tolère
 * l'erreur réseau — un logout offline réussit côté UI même si
 * l'API n'a pas répondu (le cookie sera renouvelable mais peut être
 * intentionnellement effacé côté serveur plus tard).
 */
export async function logoutAndUpdateSession(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    // Logout offline = on quand même reset le state local. Le cookie
    // côté serveur restera valide jusqu'à expiration (30 j) mais
    // l'UX locale est « déconnecté ».
  }
  store.setState({ status: 'anonymous', user: null, offline: false });
}

/**
 * @internal pour tests : reset le state initial + permet de
 * relancer initSession.
 */
export function _resetSessionForTests(state: Partial<SessionState> = {}): void {
  initStarted = false;
  store.setState(
    {
      status: state.status ?? 'loading',
      user: state.user ?? null,
      offline: state.offline ?? false,
    },
    true,
  );
}
